/**
 * Wishlist.
 *
 * Shopify has no native wishlist. This stores handles in localStorage so it
 * works for logged-out visitors with no dependency, and merges with the
 * customer's saved list when they are signed in.
 *
 * Reading the saved list is done server-side in Liquid from the customer
 * metafield. WRITING a customer metafield is not possible from a theme —
 * it needs an App Proxy or the Customer Account API. Set the proxy path in
 * `window.themeSettings.wishlistSyncUrl` and this module will POST the list
 * there on every change; without it the wishlist stays per-browser.
 */

const Wishlist = {
  key: 'femme-finesse:wishlist',

  read() {
    try {
      const raw = localStorage.getItem(this.key);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      // Private mode or blocked storage — behave as an empty wishlist.
      return [];
    }
  },

  write(handles) {
    try {
      localStorage.setItem(this.key, JSON.stringify(handles));
    } catch (error) {
      // Nothing to do — the in-page state below still reflects the change.
    }
    this.sync(handles);
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { handles: handles } }));
  },

  has(handle) {
    return this.read().indexOf(handle) !== -1;
  },

  toggle(handle) {
    const handles = this.read();
    const index = handles.indexOf(handle);
    if (index === -1) {
      handles.push(handle);
    } else {
      handles.splice(index, 1);
    }
    this.write(handles);
    return index === -1;
  },

  /** Merges the server-rendered customer list with whatever is local. */
  merge(serverHandles) {
    if (!Array.isArray(serverHandles) || !serverHandles.length) return;
    const merged = Array.from(new Set(this.read().concat(serverHandles)));
    this.write(merged);
  },

  async sync(handles) {
    const url = (window.themeSettings || {}).wishlistSyncUrl;
    if (!url || !(window.themeSettings || {}).customerLoggedIn) return;
    try {
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wishlist: handles }),
      });
    } catch (error) {
      // Sync is best-effort; the local list remains the source of truth.
    }
  },
};

window.Wishlist = Wishlist;

/* ------------------------------------------------------------------ *
 * Toggle buttons
 * ------------------------------------------------------------------ */

function paintToggle(button) {
  const handle = button.dataset.wishlistHandle;
  const active = Wishlist.has(handle);
  button.setAttribute('aria-pressed', String(active));

  const label = button.querySelector('[data-wishlist-label]');
  if (label) {
    label.textContent = active
      ? Theme.strings.wishlistRemove || 'Remove from wishlist'
      : Theme.strings.wishlistAdd || 'Add to wishlist';
  } else {
    button.setAttribute(
      'aria-label',
      active
        ? Theme.strings.wishlistRemove || 'Remove from wishlist'
        : Theme.strings.wishlistAdd || 'Add to wishlist'
    );
  }
}

function paintAll() {
  document.querySelectorAll('[data-wishlist-toggle]').forEach(paintToggle);
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-wishlist-toggle]');
  if (!button) return;
  event.preventDefault();
  Wishlist.toggle(button.dataset.wishlistHandle);
  paintAll();
});

document.addEventListener('DOMContentLoaded', () => {
  // Server-rendered customer list, if the metafield holds one.
  const seed = document.getElementById('WishlistSeed');
  if (seed) {
    try {
      Wishlist.merge(JSON.parse(seed.textContent));
    } catch (error) {
      // Malformed metafield — ignore and keep the local list.
    }
  }
  paintAll();
});

/* ------------------------------------------------------------------ *
 * <wishlist-page> — renders saved products from their handles
 * ------------------------------------------------------------------ */

class WishlistPage extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.countTarget = this.querySelector('[data-wishlist-count]');

    this.render();
    document.addEventListener('wishlist:updated', () => this.render());
  }

  async render() {
    const handles = Wishlist.read();

    if (this.countTarget) this.countTarget.textContent = handles.length;
    if (this.empty) this.empty.hidden = handles.length > 0;
    if (!this.grid) return;

    if (!handles.length) {
      this.grid.innerHTML = '';
      this.grid.hidden = true;
      return;
    }

    this.grid.hidden = false;
    this.toggleAttribute('data-loading', true);

    // Each card is rendered by the theme itself, so saved items look
    // identical to every other product card on the site.
    const cards = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(
            Theme.routes.root_url.replace(/\/$/, '') +
              '/products/' +
              handle +
              '?section_id=' +
              this.dataset.cardSection
          );
          if (!response.ok) return null;
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const card = parsed.querySelector('.card');
          return card ? card.outerHTML : null;
        } catch (error) {
          return null;
        }
      })
    );

    const found = cards.filter(Boolean);
    this.grid.innerHTML = found.join('');

    // Drop handles that no longer resolve to a live product.
    if (found.length !== handles.length) {
      const stillValid = handles.filter((handle, index) => cards[index]);
      Wishlist.write(stillValid);
    }

    this.removeAttribute('data-loading');
    paintAll();
  }
}
customElements.define('wishlist-page', WishlistPage);
